import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  IDialog,
  IDialogOptions
} from '../../../../shared/secured-shared/dialog/dialog.service';
import {
  EpisodeScheduleType,
  IEpisodeMonthRecurrence,
  IEpisodeWeekRecurrence,
  MonthRecurrenceType,
  MonthWeekDayRecurrenceType,
  RecurrenceType,
  WeekDay
} from 'src/app/core/services/extended-program-guide.service';
import { MatSelectChange } from '@angular/material/select';
import { DateTime } from 'luxon';
import { MatRadioChange } from '@angular/material/radio';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { formatRecurringEpisodeSchedule } from '../extended-program-guide.utils';
import getRecurringScheduleOccurrences from '../utils/getRecurringScheduleOccurrences';
import checkEpisodeOverlap, { IEventRange } from '../utils/checkEpisodeOverlap';
import formatScheduleOccurrencesCreationError from '../utils/formatScheduleOccurrencesCreationError';
import formatOccurrenceCollision from '../utils/formatOccurrenceCollision';
import IFormattedError from '../utils';
import {
  IEpisodeRecurrenceOptionsDialogData,
  IRecurringEpisodeViewData
} from 'src/app/core/utils/episode-details-utils';

@Component({
    selector: 'app-episode-recurrence-options',
    templateUrl: './episode-recurrence-options.component.html',
    styleUrls: ['./episode-recurrence-options.component.scss'],
    standalone: false
})
export class EpisodeRecurrenceOptionsComponent
  implements OnInit, OnDestroy, IDialog
{
  private recurrenceFormSubscription: Subscription;
  private monthlyRecurrenceMonthDaySubscription: Subscription;
  public recurrenceSettingsError: string | null = null;

  constructor() {
    this.minDate = DateTime.now().toJSDate();
  }

  public data: IEpisodeRecurrenceOptionsDialogData;

  minDate: Date;
  recurrenceForm = new FormGroup({
    startDate: new FormControl(new Date(), [Validators.required]),
    endDate: new FormControl(new Date(), [Validators.required]),
    repeatNumber: new FormControl(1, [
      Validators.required,
      Validators.min(1),
      Validators.max(99),
      (c) => this.integerValidator(c)
    ])
  });

  // Recurrence type selection
  public integerValidator(control: AbstractControl) {
    if (control.value % 1 !== 0) {
      return { integer: true };
    }
    return null;
  }
  public onRepeatNumberChange() {
    this.data.schedule.recurrence.repeat =
      this.recurrenceForm.controls.repeatNumber.value;
  }
  public recurrenceTypeOptions = [
    { label: 'Day', value: RecurrenceType.Day },
    { label: 'Week', value: RecurrenceType.Week },
    { label: 'Month', value: RecurrenceType.Month }
  ];
  public RecurrenceType = RecurrenceType;
  public onChangeRecurrenceType(event: MatSelectChange) {
    switch (event.value as RecurrenceType) {
      case RecurrenceType.Day:
        this.data.schedule.recurrence = {
          type: RecurrenceType.Day,
          repeat: this.recurrenceForm.controls.repeatNumber.value
        };
        break;
      case RecurrenceType.Week:
        this.data.schedule.recurrence = {
          type: RecurrenceType.Week,
          repeat: this.recurrenceForm.controls.repeatNumber.value,
          week_days: []
        };
        this.daysOfWeek.forEach((day) => {
          if (day.selected) {
            (
              this.data.schedule.recurrence as IEpisodeWeekRecurrence
            ).week_days.push(day.value);
          }
        });
        if (this.data.schedule.recurrence.week_days.length === 0) {
          const startDate = DateTime.fromJSDate(this.data.startDate);
          const weekday = startDate.weekday;
          this.data.schedule.recurrence.week_days.push(weekday);
          this.daysOfWeek[weekday].selected = true;
        }
        break;
      case RecurrenceType.Month:
        this.selectedMonthDay.enable();
        this.data.schedule.recurrence = {
          type: RecurrenceType.Month,
          repeat: this.recurrenceForm.controls.repeatNumber.value,
          recurrence: {
            type: MonthRecurrenceType.Day,
            day: this.selectedMonthDay.value
          }
        };
        break;
    }
    this.checkFormValidation();
  }

  // Weekly recurrence
  public daysOfWeek = [
    {
      label: 'S',
      tooltip: 'Sunday',
      value: WeekDay.Sunday,
      selected: false
    },
    {
      label: 'M',
      tooltip: 'Monday',
      value: WeekDay.Monday,
      selected: false
    },
    {
      label: 'T',
      tooltip: 'Tuesday',
      value: WeekDay.Tuesday,
      selected: false
    },
    {
      label: 'W',
      tooltip: 'Wednesday',
      value: WeekDay.Wednesday,
      selected: false
    },
    {
      label: 'T',
      tooltip: 'Thursday',
      value: WeekDay.Thursday,
      selected: false
    },
    {
      label: 'F',
      tooltip: 'Friday',
      value: WeekDay.Friday,
      selected: false
    },
    {
      label: 'S',
      tooltip: 'Saturday',
      value: WeekDay.Saturday,
      selected: false
    }
  ];
  public toggleDay(day: {
    label: string;
    tooltip: string;
    value: WeekDay;
    selected: boolean;
  }) {
    if (!day.selected) {
      day.selected = true;
      (this.data.schedule.recurrence as IEpisodeWeekRecurrence).week_days.push(
        day.value
      );
      //sort week days
      (this.data.schedule.recurrence as IEpisodeWeekRecurrence).week_days.sort(
        (a, b) => a - b
      );
    } else {
      // Only remove day if there are more than one selected
      if (
        (this.data.schedule.recurrence as IEpisodeWeekRecurrence).week_days
          .length > 1
      ) {
        day.selected = false;
        const index = (
          this.data.schedule.recurrence as IEpisodeWeekRecurrence
        ).week_days.indexOf(day.value);
        if (index >= 0) {
          (
            this.data.schedule.recurrence as IEpisodeWeekRecurrence
          ).week_days.splice(index, 1);
        }
      }
    }
    this.checkFormValidation();
  }

  // Monthly recurrence
  public MonthRecurrenceType = MonthRecurrenceType;
  public ordinalOptions = [
    { label: 'First', value: MonthWeekDayRecurrenceType.First },
    { label: 'Second', value: MonthWeekDayRecurrenceType.Second },
    { label: 'Third', value: MonthWeekDayRecurrenceType.Third },
    { label: 'Fourth', value: MonthWeekDayRecurrenceType.Fourth },
    { label: 'Last', value: MonthWeekDayRecurrenceType.Last }
  ];
  public weekDaysOptions = [
    { label: 'Monday', value: WeekDay.Monday },
    { label: 'Tuesday', value: WeekDay.Tuesday },
    { label: 'Wednesday', value: WeekDay.Wednesday },
    { label: 'Thursday', value: WeekDay.Thursday },
    { label: 'Friday', value: WeekDay.Friday },
    { label: 'Saturday', value: WeekDay.Saturday },
    { label: 'Sunday', value: WeekDay.Sunday }
  ];
  public selectedOrdinal: MonthWeekDayRecurrenceType =
    MonthWeekDayRecurrenceType.First;
  public selectedWeekDay: WeekDay = WeekDay.Monday;
  public selectedMonthDay = new FormControl(1, [
    Validators.required,
    Validators.min(1),
    Validators.max(31),
    (c) => this.integerValidator(c)
  ]);
  public onRecurrenceWeekDayChange() {
    (this.data.schedule.recurrence as IEpisodeMonthRecurrence).recurrence = {
      type: MonthRecurrenceType.WeekDay,
      order: this.selectedOrdinal,
      week_day: this.selectedWeekDay
    };
    this.checkFormValidation();
  }
  public monthlyRecurrenceTypeChange(event: MatRadioChange) {
    if (this.data.schedule.recurrence.type === RecurrenceType.Month) {
      switch (event.value as MonthRecurrenceType) {
        case MonthRecurrenceType.Day:
          (
            this.data.schedule.recurrence as IEpisodeMonthRecurrence
          ).recurrence = {
            type: MonthRecurrenceType.Day,
            day: this.selectedMonthDay.value
          };
          this.selectedMonthDay.enable();
          break;
        case MonthRecurrenceType.WeekDay:
          (
            this.data.schedule.recurrence as IEpisodeMonthRecurrence
          ).recurrence = {
            type: MonthRecurrenceType.WeekDay,
            order: this.selectedOrdinal,
            week_day: this.selectedWeekDay
          };
          this.selectedMonthDay.disable();
          break;
      }
    }
  }

  public formatRecurringEpisodeSchedule(
    recurringData: IRecurringEpisodeViewData,
    startDate: Date,
    startTime: string
  ) {
    return formatRecurringEpisodeSchedule(recurringData, startDate, startTime);
  }

  public repeatsIndefinitely = false;
  public previousSelectedEndDate: Date;

  public onRepeatsIndefinitelyChange() {
    if (this.repeatsIndefinitely) {
      this.previousSelectedEndDate = this.recurrenceForm.controls.endDate.value;
      this.recurrenceForm.controls.endDate.setValue(null);
      this.recurrenceForm.controls.endDate.disable();
      this.data.schedule.end.date = null;
    } else {
      this.recurrenceForm.controls.endDate.enable();
      this.recurrenceForm.controls.endDate.setValue(
        this.previousSelectedEndDate
      );
      this.data.schedule.end.date = this.recurrenceForm.controls.endDate.value;
    }
  }

  get recurrenceSettingsMessage(): string {
    if (this.invalidSelectedMonthDay || this.recurrenceForm.invalid) {
      return 'Please fill in all required fields';
    }
    return this.formatRecurringEpisodeSchedule(
      this.data.schedule,
      this.data.startDate,
      this.data.startTime
    );
  }

  public updateData() {
    this.data.startDate = this.recurrenceForm.controls.startDate.value;
    this.data.schedule.end.date = this.recurrenceForm.controls.endDate.value;
    this.data.schedule.recurrence.repeat =
      this.recurrenceForm.controls.repeatNumber.value;
  }

  public get invalidSelectedMonthDay() {
    return (
      this.data.schedule.recurrence.type === RecurrenceType.Month &&
      this.data.schedule.recurrence.recurrence.type ===
        MonthRecurrenceType.Day &&
      this.selectedMonthDay.invalid
    );
  }

  public checkFormValidation() {
    this.recurrenceForm.markAllAsTouched();

    if (!this.invalidSelectedMonthDay && this.recurrenceForm.valid) {
      this.validateRecurrenceSettings();
    }

    this.dialogOptions.saveButton.disable =
      this.recurrenceSettingsError !== null ||
      this.recurrenceForm.invalid ||
      this.invalidSelectedMonthDay;
  }

  // Dialog hooks
  public close: (
    value: { schedule: IRecurringEpisodeViewData; startDate: Date } | null
  ) => void;

  public dialogOptions: IDialogOptions;

  public ngOnInit(): void {
    this.dialogOptions.deleteButton.show = false;

    this.recurrenceForm.controls.repeatNumber.setValue(
      this.data.schedule.recurrence.repeat
    );
    this.recurrenceForm.controls.startDate.setValue(this.data.startDate);

    if (this.data.schedule.end.date) {
      this.recurrenceForm.controls.endDate.setValue(
        this.data.schedule.end.date
      );
    } else {
      this.recurrenceForm.controls.endDate.disable();
    }

    switch (this.data.schedule.recurrence.type) {
      case RecurrenceType.Week:
        this.data.schedule.recurrence.week_days.forEach((day) => {
          this.daysOfWeek[day].selected = true;
        });
        break;
      case RecurrenceType.Month:
        switch (this.data.schedule.recurrence.recurrence.type) {
          case MonthRecurrenceType.Day:
            this.selectedMonthDay.setValue(
              this.data.schedule.recurrence.recurrence.day
            );
            break;
          case MonthRecurrenceType.WeekDay:
            this.selectedMonthDay.disable();
            this.selectedOrdinal =
              this.data.schedule.recurrence.recurrence.order;
            this.selectedWeekDay =
              this.data.schedule.recurrence.recurrence.week_day;
            break;
        }
        break;
    }

    this.recurrenceFormSubscription =
      this.recurrenceForm.valueChanges.subscribe(() => {
        this.updateData();
        this.checkFormValidation();
      });

    this.monthlyRecurrenceMonthDaySubscription =
      this.selectedMonthDay.valueChanges.subscribe(() => {
        if (
          this.data.schedule.recurrence.type === RecurrenceType.Month &&
          this.data.schedule.recurrence.recurrence.type ===
            MonthRecurrenceType.Day
        ) {
          (
            this.data.schedule.recurrence as IEpisodeMonthRecurrence
          ).recurrence = {
            type: MonthRecurrenceType.Day,
            day: this.selectedMonthDay.value
          };
        }
        this.checkFormValidation();
      });

    this.checkFormValidation();
  }

  public onCancel(): void {
    this.close(null);
  }

  public onConfirm(): void {
    this.close({
      schedule: this.data.schedule,
      startDate: this.data.startDate
    });
  }

  public onDelete(): void {}

  public ngOnDestroy(): void {
    this.recurrenceFormSubscription.unsubscribe();
    this.monthlyRecurrenceMonthDaySubscription.unsubscribe();
  }

  private validateRecurrenceSettings() {
    const { recurrence } = this.data.schedule;
    const startDate = DateTime.fromJSDate(this.data.startDate);
    const endDate = this.data.schedule.end.date
      ? DateTime.fromJSDate(this.data.schedule.end.date).endOf('day')
      : null;
    const occurrences = getRecurringScheduleOccurrences(
      {
        start_date: startDate.toSeconds(),
        duration: this.data.duration
      },
      {
        recurrence,
        type: EpisodeScheduleType.Recurring,
        end_date: endDate ? endDate.toSeconds() : null
      }
    );

    this.recurrenceSettingsError = null;

    if ('type' in occurrences) {
      this.recurrenceSettingsError =
        formatScheduleOccurrencesCreationError(occurrences).message;
      return;
    }

    const currentRange: IEventRange = {
      startDate,
      endDate
    };

    for (const ep of this.data.episodes) {
      const { schedule } = ep;
      let err: IFormattedError | null;
      switch (schedule.type) {
        case EpisodeScheduleType.Definite: {
          const result = checkEpisodeOverlap(currentRange, [
            {
              startDate: DateTime.fromSeconds(ep.start_date),
              endDate: DateTime.fromSeconds(ep.start_date).plus({
                minutes: ep.duration
              })
            }
          ]);

          err = result !== null ? formatOccurrenceCollision(result) : null;
          break;
        }
        case EpisodeScheduleType.Recurring: {
          const result = getRecurringScheduleOccurrences(
            {
              start_date: ep.start_date,
              duration: ep.duration
            },
            schedule
          );
          if ('type' in result) {
            err = formatScheduleOccurrencesCreationError(result);
          } else {
            const collision = checkEpisodeOverlap(
              currentRange,
              result.map((o) => ({
                startDate: o.startDate,
                endDate: o.endDate
              }))
            );
            if (collision !== null) {
              err = formatOccurrenceCollision(collision);
            } else {
              err = null;
            }
          }
          break;
        }
        case EpisodeScheduleType.Indefinite: {
          const collision = checkEpisodeOverlap(
            {
              startDate: DateTime.fromSeconds(ep.start_date),
              endDate: null
            },
            occurrences
          );
          if (collision !== null) {
            err = formatOccurrenceCollision(collision);
          } else {
            err = null;
          }
          break;
        }
      }
      // TODO: maybe we want to pile these errors up and show them all at once?
      if (err !== null) {
        this.recurrenceSettingsError = err.message;
        break;
      }
    }
  }
}
